/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Document, Page, pdfjs } from 'react-pdf';
import assetCache from '../utils/assetCache';
import { PDF_WORKER_SRC } from '../constants/staticCaptureAssets';

// public/pdf.worker.min.mjs is the prebuilt pdfjs-dist worker checked into
// the repo as-is (must match the installed pdfjs-dist version above) instead
// of bundled via `new URL(..., import.meta.url)`. That approach let our own
// production build re-process this vendor file, which corrupted it with
// broken absolute import paths baked in from the build server. CRA copies
// public/ verbatim, so serving it from there avoids that corruption while
// staying same-origin (no CDN dependency).
pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;

// Renders the PDF as scrollable pages in our own document instead of an
// embedded iframe. Mobile browsers' built-in PDF viewers don't reliably
// forward touch-scroll into a cross-origin iframe (a known platform
// limitation, most notably on iOS Safari), so a same-document scrollable
// container is the only way to get consistent scroll + fit-to-width
// behavior across devices.
const PdfInstructionViewer = ({ url, token, onClick, onLoad, onError }) => {
  const containerRef = useRef(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  // Live URL first (same reasoning as useCachedAssetSrc); only swapped for a
  // cached blob once loading the live URL actually fails.
  const [fileSource, setFileSource] = useState(url);
  const triedFallbackRef = useRef(false);

  // pdfjs's worker script is itself a network fetch (see PDF_WORKER_SRC's
  // doc comment) — and its own main-thread fallback re-fetches that exact
  // same URL, so being offline defeats both paths unless we point
  // GlobalWorkerOptions at a cached blob first. This must resolve (and
  // must be applied) *before* <Document> below ever calls pdfjs.getDocument,
  // so its render is gated on workerReady rather than raced against it.
  const [workerReady, setWorkerReady] = useState(false);
  const workerObjectUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!navigator.onLine && token) {
        const workerBlob = await assetCache.getCachedAssetBlob(token, PDF_WORKER_SRC);
        if (!cancelled && workerBlob) {
          const objectUrl = URL.createObjectURL(workerBlob);
          workerObjectUrlRef.current = objectUrl;
          pdfjs.GlobalWorkerOptions.workerSrc = objectUrl;
        }
      } else {
        pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
      }
      if (!cancelled) setWorkerReady(true);
    })();
    return () => {
      cancelled = true;
      if (workerObjectUrlRef.current) {
        URL.revokeObjectURL(workerObjectUrlRef.current);
        workerObjectUrlRef.current = null;
      }
    };
  }, [token]);

  useEffect(() => {
    setFileSource(url);
    triedFallbackRef.current = false;
  }, [url]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => setPageWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleLoadError = async (error) => {
    if (!triedFallbackRef.current && token && url) {
      triedFallbackRef.current = true;
      const blob = await assetCache.getCachedAssetBlob(token, url);
      if (blob) {
        setFileSource(blob);
        return;
      }
    }
    onError?.(error);
  };

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      style={{
        position: 'relative',
        width: '92%',
        height: '90%',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: 'white',
        borderRadius: 8,
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {workerReady && (
        <Document
          file={fileSource}
          loading={(
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="w-8 h-8 border-4 border-black/20 border-t-black rounded-full animate-spin" />
            </div>
          )}
          error={null}
          onLoadSuccess={({ numPages: loadedPages }) => setNumPages(loadedPages)}
          onLoadError={handleLoadError}
        >
          {pageWidth > 0 && Array.from({ length: numPages }, (_, index) => (
            <Page
              key={index}
              pageNumber={index + 1}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              onRenderSuccess={index === 0 ? onLoad : undefined}
            />
          ))}
        </Document>
      )}
    </div>
  );
};

PdfInstructionViewer.propTypes = {
  url: PropTypes.string.isRequired,
  token: PropTypes.string,
  onClick: PropTypes.func,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
};

export default PdfInstructionViewer;
